import { useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import { useGlobalStateContext } from "@/context/GlobalContext";
import { useIsVendor } from "@/hooks/useIsVendor";
import {
  resolveActiveProjectRecordForProductId,
  resolveVendorDetailsListForProduct,
} from "@/utils/activeProjectProductUtils";
import {
  getVendorAssessmentIdForTabWithRowFallback,
  getActiveAssessment,
} from "@/utils/productInfoDetailsSelectors";

export type UseAllergensVendorContextParams = {
  productId: string | number;
  variantId: string | number;
  activeVariantIndex?: number;
};

export type UseAllergensVendorContextResult = {
  productSpecificationType: string;
  normalizedSpecType: string;
  isPetFood: boolean;
  activeProjectForRoute: ReturnType<typeof resolveActiveProjectRecordForProductId>;
  vendorListForFlush: ReturnType<typeof resolveVendorDetailsListForProduct>;
  effectiveVendorGroupIndexForFlush: number;
  vendorAssessmentIdForFlushRow: string | null;
  currentVendorStatus: string;
  workflowStatus: string[];
  normalizedVendorId: string | null;
  productSpecificationId: string | number | null;
};

export function useAllergensVendorContext({
  productId,
  variantId,
  activeVariantIndex,
}: UseAllergensVendorContextParams): UseAllergensVendorContextResult {
  const { userDetails } = useAuth();
  const isVendor = useIsVendor();
  const {
    selectedActiveProject,
    selectedActiveProjectSelectedVendor,
    activeProjectsProductInfo,
    activeVendorTab,
    activeVendorVersionIndex,
    ideaVendorTabRedirectSnapshot,
  } = useGlobalStateContext();

  const productSpecificationType = (selectedActiveProject?.productSpecificationType ||
    selectedActiveProject?.category ||
    "") as string;

  const normalizedSpecType = useMemo(() => {
    return ((selectedActiveProject?.productSpecificationType || "") as string)
      .replace(/&amp;/g, "&")
      .trim();
  }, [selectedActiveProject?.productSpecificationType]);

  const isPetFood = normalizedSpecType === "Pet Foods";

  const activeProjectForRoute = useMemo(
    () =>
      resolveActiveProjectRecordForProductId(
        productId,
        selectedActiveProject,
        activeProjectsProductInfo
      ),
    [productId, selectedActiveProject, activeProjectsProductInfo]
  );

  const vendorListForFlush = useMemo(
    () =>
      resolveVendorDetailsListForProduct({
        productId,
        sources: [selectedActiveProject, activeProjectsProductInfo],
        ideaVendorTabRedirectSnapshot,
      }),
    [productId, selectedActiveProject, activeProjectsProductInfo, ideaVendorTabRedirectSnapshot]
  );

  const effectiveVendorGroupIndexForFlush = useMemo(() => {
    if (!isVendor) {
      return typeof activeVendorTab === "number" ? activeVendorTab : 0;
    }
    let idx = typeof activeVendorTab === "number" ? activeVendorTab : 0;
    const loggedInVendorId = (
      userDetails as { vendorUser?: { vendorId?: string | number } } | undefined
    )?.vendorUser?.vendorId;
    if (loggedInVendorId != null && String(loggedInVendorId).trim() !== "") {
      const found = vendorListForFlush.findIndex(
        (v: unknown) =>
          String(
            (v as Record<string, unknown>)?.vendorId ??
              (v as Record<string, unknown>)?.id ??
              ""
          ) === String(loggedInVendorId)
      );
      if (found >= 0) {
        idx = found;
      }
    }
    return idx;
  }, [isVendor, activeVendorTab, vendorListForFlush, userDetails]);

  const vendorAssessmentIdForFlushRow = useMemo(() => {
    const record = (activeProjectForRoute ?? selectedActiveProject) as
      | Record<string, unknown>
      | null
      | undefined;
    const fromProductInfo = activeProjectsProductInfo as
      | Record<string, unknown>
      | null
      | undefined;
    const selectedRec = selectedActiveProject as Record<string, unknown> | null | undefined;
    const tab = effectiveVendorGroupIndexForFlush;
    const vi = activeVendorVersionIndex ?? 0;
    const id =
      getVendorAssessmentIdForTabWithRowFallback(record, tab, vi) ||
      getVendorAssessmentIdForTabWithRowFallback(fromProductInfo, tab, vi) ||
      getVendorAssessmentIdForTabWithRowFallback(selectedRec, tab, vi);
    return id || null;
  }, [
    activeProjectForRoute,
    selectedActiveProject,
    activeProjectsProductInfo,
    effectiveVendorGroupIndexForFlush,
    activeVendorVersionIndex,
  ]);

  const { currentVendorStatus, workflowStatus } = useMemo(() => {
    const tab = activeVendorTab || 0;
    const src =
      (selectedActiveProject as Record<string, unknown> | null | undefined) ??
      (activeProjectsProductInfo as Record<string, unknown> | null | undefined);
    const merged = getActiveAssessment(src, tab, activeVendorVersionIndex ?? 0) as {
      vendorAssessmentStatus?: string;
      workflowStatus?: unknown[];
    } | null;
    if (!merged) {
      return { currentVendorStatus: "", workflowStatus: [] as string[] };
    }
    const wf = merged.workflowStatus;
    return {
      currentVendorStatus: String(merged.vendorAssessmentStatus ?? ""),
      workflowStatus: (Array.isArray(wf) ? wf.map((s) => String(s)) : []) as string[],
    };
  }, [selectedActiveProject, activeProjectsProductInfo, activeVendorTab, activeVendorVersionIndex]);

  const normalizedVendorId = selectedActiveProjectSelectedVendor
    ? String(selectedActiveProjectSelectedVendor)
    : null;

  const productSpecificationId = useMemo(() => {
    try {
      const info = activeProjectsProductInfo;
      if (!info || !Array.isArray(info?.vendorDetails) || info.vendorDetails.length === 0) {
        console.log("⚠️ [productSpecificationId] No vendor details available");
        return null;
      }

      const selectedVendorId = String(selectedActiveProjectSelectedVendor ?? "");
      const vendor =
        info.vendorDetails.find((v: any) => String(v?.vendorId) === selectedVendorId) ||
        info.vendorDetails[0];
      const specs: any[] = vendor?.vendorIngredientDetails?.productSpecification || [];

      if (!Array.isArray(specs) || specs.length === 0) {
        console.log("⚠️ [productSpecificationId] No product specifications available");
        return null;
      }

      let index = 0;

      if (activeVariantIndex !== undefined && activeVariantIndex !== null) {
        index = activeVariantIndex;
      } else {
        const variants: any[] = Array.isArray((selectedActiveProject as any)?.variants)
          ? (selectedActiveProject as any).variants
          : [];

        if (
          variants.length > 0 &&
          (variantId ?? null) !== null &&
          (variantId ?? undefined) !== undefined
        ) {
          const idx = variants.findIndex((v: any) => String(v?.id) === String(variantId));
          index = idx >= 0 ? idx : 0;
        } else {
          console.log(
            "⚠️ [productSpecificationId] No variants array or variantId, defaulting to index 0"
          );
        }
      }

      const spec = specs[index] || specs[0];
      const specId = spec?.id ?? null;

      return specId;
    } catch (error) {
      console.error("❌ [productSpecificationId] Error:", error);
      return null;
    }
  }, [
    activeProjectsProductInfo,
    selectedActiveProject,
    selectedActiveProjectSelectedVendor,
    variantId,
    activeVariantIndex,
  ]);

  return {
    productSpecificationType,
    normalizedSpecType,
    isPetFood,
    activeProjectForRoute,
    vendorListForFlush,
    effectiveVendorGroupIndexForFlush,
    vendorAssessmentIdForFlushRow,
    currentVendorStatus,
    workflowStatus,
    normalizedVendorId,
    productSpecificationId,
  };
}
